/**
 * ================================================================
 * SESSION MANAGER: Centralized Session Persistence
 * ================================================================
 *
 * @fileOverview     Central session management for storage synchronization
 * @module           lib/session/sessionManager
 * @version          2.0.0
 * @since            2025-08-05
 * @author           Development Team <dev@company.com>
 *
 * @purpose          Single point of storage interaction for session data
 * @dependencies     Encryption utilities, Redux types
 * @exports          Hydration and persistence functions
 *
 * @architecture
 * - Hydration: Load session data from storage on app init
 * - Persistence: Save session changes to storage
 * - Encryption: Secure storage of sensitive data
 * - Validation: Ensure data integrity and expiry
 * - Redux Integration: Works seamlessly with Redux session management
 *
 * @security
 * - Token encryption: All tokens encrypted before storage
 * - Session validation: Check token expiry and format
 * - Cleanup: Remove expired or invalid sessions
 *
 * @migration        Updated to work with Redux-based session management
 *                   Deprecated functions removed, focus on storage layer only
 *
 * ================================================================
 */

import { decryptData, encryptData } from '@/lib/utils/encryption';
import type { SessionHydrationData, StoredPreferences, StoredSessionData } from '@/types/session';

// ================================================================
// STORAGE CONFIGURATION
// ================================================================

const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  USER_DATA: 'userData',
  USER_PREFERENCES: 'userPreferences',
  SESSION_METADATA: 'sessionMetadata',
} as const;

const ENCRYPTION_SALT = import.meta.env.VITE_APP_SALT as string;

// ================================================================
// STORAGE UTILITIES
// ================================================================

/**
 * Safely get encrypted data from sessionStorage
 */
const getEncryptedSessionData = (key: string): any => {
  try {
    const data = sessionStorage.getItem(key);
    if (!data) return null;

    return decryptData(data, ENCRYPTION_SALT);
  } catch (error) {
    console.warn(`Failed to decrypt session data for key: ${key}`, error);
    return null;
  }
};

/**
 * Safely store encrypted data to sessionStorage
 */
const setEncryptedSessionData = (key: string, value: any): boolean => {
  try {
    const encryptedData = encryptData(value, ENCRYPTION_SALT);
    sessionStorage.setItem(key, encryptedData);
    return true;
  } catch (error) {
    console.warn(`Failed to encrypt and store session data for key: ${key}`, error);
    return false;
  }
};

/**
 * Safely get data from localStorage
 */
const getLocalStorageData = (key: string): any => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.warn(`Failed to read localStorage data for key: ${key}`, error);
    return null;
  }
};

/**
 * Safely store data to localStorage
 */
const setLocalStorageData = (key: string, value: any): boolean => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Failed to store localStorage data for key: ${key}`, error);
    return false;
  }
};

// ================================================================
// SESSION VALIDATION
// ================================================================

/**
 * Validate session data integrity and expiry
 */
const validateSessionData = (sessionData: StoredSessionData): boolean => {
  if (!sessionData || !sessionData.authToken || !sessionData.user) {
    return false;
  }

  // Check if session has expired
  if (sessionData.sessionExpiryTime && Date.now() > sessionData.sessionExpiryTime) {
    console.log('Session expired, clearing data');
    return false;
  }

  // Basic token format validation (adjust based on your token format)
  if (sessionData.authToken.length < 10) {
    console.log('Invalid token format');
    return false;
  }

  return true;
};

// ================================================================
// HYDRATION FUNCTIONS
// ================================================================

/**
 * Hydrate session state from storage on app initialization
 */
export const hydrateSessionFromStorage = (): SessionHydrationData => {
  console.log('🔄 Hydrating session from storage...');

  try {
    // Load session data from sessionStorage
    const storedSessionData = getEncryptedSessionData(STORAGE_KEYS.USER_DATA);
    const storedAuthToken = sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

    // Load preferences from localStorage (persistent across sessions)
    const storedPreferences = getLocalStorageData(STORAGE_KEYS.USER_PREFERENCES);

    // Reconstruct session data
    let sessionData: StoredSessionData | null = null;

    if (storedSessionData && storedAuthToken) {
      sessionData = {
        ...storedSessionData,
        authToken: storedAuthToken,
        lastActivityTimestamp: Date.now(), // Update activity on hydration
      };

      // Validate session data
      if (sessionData && !validateSessionData(sessionData)) {
        console.log('⚠️ Invalid session data found, clearing...');
        clearStoredSession();
        sessionData = null;
      }
    }

    // Merge preferences with defaults
    const preferences: StoredPreferences = {
      theme: 'system',
      sidebarCollapsed: false,
      notifications: true,
      autoLogout: true,
      sessionTimeout: 30,
      ...(storedPreferences || {}),
    };

    console.log('✅ Session hydration complete:', {
      hasSession: !!sessionData,
      isAuthenticated: !!sessionData?.isAuthenticated,
      user: sessionData?.user?.branch_code || 'none',
    });

    return {
      session: sessionData,
      preferences,
    };
  } catch (error) {
    console.error('❌ Error during session hydration:', error);
    clearStoredSession();
    return {
      session: null,
      preferences: null,
    };
  }
};

// ================================================================
// PERSISTENCE FUNCTIONS
// ================================================================

/**
 * Persist session data to storage
 */
export const persistSessionToStorage = (sessionData: StoredSessionData): boolean => {
  try {
    if (!sessionData.authToken || !sessionData.user) {
      console.log('⚠️ Attempting to persist invalid session data');
      return false;
    }

    // Store auth token separately for easy access
    sessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, sessionData.authToken);

    // Store encrypted user data and session metadata
    const userDataToStore = {
      user: sessionData.user,
      isAuthenticated: sessionData.isAuthenticated,
      loginTimestamp: sessionData.loginTimestamp,
      lastActivityTimestamp: sessionData.lastActivityTimestamp,
      sessionExpiryTime: sessionData.sessionExpiryTime,
    };

    const success = setEncryptedSessionData(STORAGE_KEYS.USER_DATA, userDataToStore);

    if (success) {
      console.log('✅ Session persisted to storage');
    } else {
      console.log('❌ Failed to persist session to storage');
    }

    return success;
  } catch (error) {
    console.error('❌ Error persisting session:', error);
    return false;
  }
};

/**
 * Persist user preferences to storage
 */
export const persistPreferencesToStorage = (preferences: StoredPreferences): boolean => {
  try {
    const success = setLocalStorageData(STORAGE_KEYS.USER_PREFERENCES, preferences);

    if (success) {
      console.log('✅ Preferences persisted to storage');
    } else {
      console.log('❌ Failed to persist preferences to storage');
    }

    return success;
  } catch (error) {
    console.error('❌ Error persisting preferences:', error);
    return false;
  }
};

// ================================================================
// CLEANUP FUNCTIONS
// ================================================================

/**
 * Clear all stored session data
 */
export const clearStoredSession = (): void => {
  try {
    // Clear session storage
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
    sessionStorage.removeItem(STORAGE_KEYS.SESSION_METADATA);

    // Clear user-specific data from localStorage
    localStorage.removeItem('userPreferences');
    localStorage.removeItem('appCache');

    // Clear any temporary session data
    sessionStorage.clear();

    console.log('🗑️ Session storage cleared');
  } catch (error) {
    console.error('❌ Error clearing session storage:', error);
  }
};

/**
 * Clear all stored data including preferences
 */
export const clearAllStoredData = (): void => {
  try {
    clearStoredSession();
    localStorage.removeItem(STORAGE_KEYS.USER_PREFERENCES);
    console.log('🗑️ All stored data cleared');
  } catch (error) {
    console.error('❌ Error clearing all stored data:', error);
  }
};

// ================================================================
// TOKEN UTILITIES (Centralized token access)
// ================================================================

/**
 * Get current auth token from storage (for compatibility during migration)
 * @deprecated Use Redux state instead
 */
export const getCurrentAuthToken = (): string | null => {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.warn('Failed to get auth token from storage:', error);
    return null;
  }
};

/**
 * Prepare auth headers for RTK Query requests
 * Uses session manager to get current auth token
 *
 * @param headers - RTK Query headers object
 * @returns Headers with auth token if available
 */
export const prepareAuthHeaders = (headers: any) => {
  const token = getCurrentAuthToken();
  if (token) {
    headers.set('AuthToken', token);
  }
  return headers;
};

/**
 * ================================================================
 * END OF SESSION MANAGER
 * ================================================================
 *
 * @exports
 * - hydrateSessionFromStorage: Load session from storage on app init
 * - persistSessionToStorage: Save session data to storage
 * - persistPreferencesToStorage: Save user preferences
 * - clearStoredSession: Clear session data
 * - clearAllStoredData: Clear all stored data
 * - getCurrentAuthToken: Get auth token (deprecated)
 *
 * @usage
 * - App initialization: Call hydrateSessionFromStorage()
 * - Login: Call persistSessionToStorage()
 * - Logout: Call clearStoredSession()
 * - Preferences update: Call persistPreferencesToStorage()
 *
 * ================================================================
 */
